import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Col,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import Alert from "../../common/Alert";
import {
  DANGER,
  EMPTY,
  STAFF_SESSION_EXPIRED,
} from "../../../configurations/Constants";
import {
  ERROR_UPDATING_WELL_CONNECTION_DETAILS,
  INCOMING_TOKEN_HAS_EXPIRED,
  UPDATE_WELL_CONNECTION_INFO,
} from "../../../configurations/MessageConstants";
import SessionHelper from "../../../helpers/SessionHelper";
import WellReportService from "../../../api-service/WellReportService";
import StorageHelper from "../../../helpers/StorageHelper";
import { StatusCodes } from "../../../configurations/status-codes";
import ConfirmationPopup from "./ConfirmationPopup";
import SuccessPopup from "./SuccessPopup";

function WellConnectionUpdate({
  showUpdateModal,
  closeUpdateModal,
  selectedWell,
  setWellConnectionDetailsUpdated,
}) {
  const [meterId, setMeterId] = useState(EMPTY);
  const [wellId, setWellId] = useState(EMPTY);
  const [originalMeterId, setOriginalMeterId] = useState(EMPTY);
  const [originalAccountId, setOriginalAccountId] = useState(EMPTY);
  const [accountId, setAccountId] = useState(EMPTY);
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [variant, setVariant] = useState(EMPTY);
  const [showAlert, setShowAlert] = useState(false);
  const [validated, setValidated] = useState(false);
  const [confirmBtnClick, setConfirmBtnClick] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    setWellId(selectedWell.well_id);
    const meterID =
      selectedWell.meter_id === null ? EMPTY : selectedWell.meter_id;
    const accountID =
      selectedWell.account_id === null ? EMPTY : selectedWell.account_id;
    setMeterId(meterID);
    setOriginalMeterId(meterID);
    setAccountId(accountID);
    setOriginalAccountId(accountID);
  }, []);

  const handleClose = () => {
    setAlertMessage(EMPTY);
    setVariant(EMPTY);
    setShowAlert(false);
    setMeterId(EMPTY);
    setAccountId(EMPTY);
    setWellId(EMPTY);
    closeUpdateModal();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "meterId") setMeterId(value);
    else if (name === "accountId") setAccountId(value);

    if (showConfirmation) {
      setShowConfirmation(false);
      setShowAlert(false);
      setConfirmBtnClick(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(EMPTY);
    setVariant(EMPTY);
    setShowAlert(false);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const handleValidation = (event) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      setShowConfirmationModal(true);
    }
    setValidated(true);
  };

  const HandleUpdateConnectionDetails = async () => {
    setConfirmBtnClick(true);
    const payload = {
      well_id: wellId,
      meter_id: meterId,
      account_id: accountId,
    };
    try {
      await WellReportService.updateWellConnectionDetails(payload);
      setShowConfirmationModal(false);
      setWellConnectionDetailsUpdated(true);
      setShowSuccessModal(true);
    } catch (error) {
      console.error(ERROR_UPDATING_WELL_CONNECTION_DETAILS, error);
      const responseData = error.response?.data;
      if (
        error.response?.status === StatusCodes.UNAUTHORIZED &&
        responseData?.message?.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      } else if (error.response.status === StatusCodes.BAD_REQUEST) {
        setAlertMessage(responseData.message);
        setVariant(DANGER);
        setShowAlert(true);
        setConfirmBtnClick(false);
        setShowConfirmationModal(false);
      }
    }
  };

  return (
    <div>
      <Modal
        show={showUpdateModal && !showConfirmationModal && !showSuccessModal}
        onHide={handleClose}
        className="well-connection-review"
      >
        <Modal.Header closeButton>
          <Modal.Title className="m-auto p-auto">
            Update Well Connection Report
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="bottom"
              overlay={
                <Popover id="info-popover">
                  <Popover.Body>
                    <p>{UPDATE_WELL_CONNECTION_INFO}</p>
                  </Popover.Body>
                </Popover>
              }
            >
              <Button
                variant="secondary"
                className="small-info-button info-button fa fa-info-circle"
              ></Button>
            </OverlayTrigger>
          </Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          validated={validated}
          onSubmit={(e) => handleValidation(e)}
        >
          <Modal.Body>
            {showAlert && (
              <Alert
                key="messageAlert"
                variant={variant}
                closeAlert={(e) => handleCloseAlert(e)}
                alertMessage={alertMessage}
                dismissible
              />
            )}
            <Row className="m-2 d-flex align-items-center">
              <Form.Group as={Col} controlId="formWellID" className="m-1">
                <Form.Label className="label">Well ID</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={wellId}
                  name="wellId"
                  disabled={true}
                />
              </Form.Group>
            </Row>
            <Row className="m-2 d-flex align-items-center">
              <Form.Group as={Col} controlId="formPowerCompany" className="m-1">
                <Form.Label className="label">Power Company</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={selectedWell.power_co}
                  name="powerCompany"
                  disabled={true}
                />
              </Form.Group>
            </Row>
            <Row className="m-2 d-flex align-items-center">
              <Form.Group as={Col} controlId="formMeterID" className="m-1">
                <Form.Label className="label">Meter ID</Form.Label>
                <Form.Control
                  type="text"
                  value={meterId}
                  name="meterId"
                  onChange={handleChange}
                  autoComplete="off"
                  maxLength={20}
                />
              </Form.Group>
            </Row>
            <Row className="m-2 d-flex align-items-center">
              <Form.Group as={Col} controlId="formAccountID" className="m-1">
                <Form.Label className="label">Account ID</Form.Label>
                <Form.Control
                  type="text"
                  value={accountId}
                  name="accountId"
                  onChange={handleChange}
                  autoComplete="off"
                  maxLength={20}
                />
              </Form.Group>
            </Row>
          </Modal.Body>
          <Modal.Footer className="me-3">
            <Button variant="primary" onClick={handleClose} type="button">
              Close
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                meterId === originalMeterId && accountId === originalAccountId
              }
            >
              Update
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ConfirmationPopup
        show={showConfirmationModal}
        onHide={handleCloseConfirmationModal}
        wellId={wellId}
        originalMeterId={originalMeterId}
        meterId={meterId}
        originalAccountId={originalAccountId}
        accountId={accountId}
        onConfirm={HandleUpdateConnectionDetails}
        confirmBtnClick={confirmBtnClick}
      />
      <SuccessPopup
        show={showSuccessModal}
        onHide={handleCloseSuccessModal}
        wellId={wellId}
        meterId={meterId}
        accountId={accountId}
      />
    </div>
  );
}

export default WellConnectionUpdate;
